import { Logger, type QuizData, type QuizGetSuccess, type QuizId } from '@terros/common'
import { useQueryClient } from '@tanstack/react-query'
import { quizKey, quizListKey, quizTakeKey } from '../keys'

type QuizCache = {
  clearItem: (quizId: QuizId) => void
  clearList: () => void
  setItem: (quiz: QuizData) => void
  addItem: (quiz: QuizData) => void
  clearTakeItem: (quizId: QuizId) => void
}

const logger = new Logger('quiz/cache')
// logger.level = 'verbose'

export function useQuizCache(): QuizCache {
  const queryClient = useQueryClient()
  return {
    clearItem: (quizId: QuizId) => {
      logger.verbose('clearing item', { quizId })
      queryClient.invalidateQueries({ queryKey: quizKey(quizId) })
    },
    clearList: () => {
      logger.verbose('clearing list')
      queryClient.invalidateQueries({ queryKey: quizListKey() })
    },
    setItem: (quiz: QuizData) => {
      logger.verbose('setting item', { quizId: quiz.quizId })

      queryClient.setQueryData(quizKey(quiz.quizId), (old: QuizGetSuccess) => ({ ...old, quiz }))
      queryClient.setQueriesData({ queryKey: quizListKey() }, (old: QuizData[]) => {
        if (!old) return
        return old.map((q) => (q.quizId === quiz.quizId ? quiz : q))
      })
    },
    addItem: (quiz: QuizData) => {
      logger.verbose('adding item', { quizId: quiz.quizId })
      const item: QuizGetSuccess = { quiz, questions: [], type: 'success' }
      queryClient.setQueryData(quizKey(quiz.quizId), item)
      queryClient.setQueriesData({ queryKey: quizListKey() }, (old: QuizData[]) => {
        if (!old) return
        return [...old, quiz]
      })
    },
    clearTakeItem: (quizId: QuizId) => {
      queryClient.invalidateQueries({ queryKey: quizTakeKey(quizId) })
    },
  }
}
